import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { Facing } from "./Facing";
import { ThreePlanogram } from "../store/types";
import { getItemDepthPlacement } from "src/components/Planogram/common/shelfLoaderView";
import { useSplitShift } from "../store/splitShift";
import { useEffect } from "react";
import { createThreeMesh } from "../store/utils/three";
import {
  itemFrontOverhangMarker,
  OVERHANG_COLOR,
  OVERHANG_INVALID_COLOR,
} from "src/components/Planogram/common/frontOverhangView";

interface ItemProps {
  product: PlanogramItem;
  three: ThreePlanogram;
}

export const Product = ({ product: item, three }: ItemProps) => {
  const toSceneX = useSplitShift();
  const layout = item.getLayout();

  const totalFacings = item.facings || 1;
  const rows = item.facingsRows || 1;
  const columns = Math.ceil(totalFacings / rows);

  // The planned units deep (a stated count, else what fits in the usable
  // depth), front-aligned: behind it is free space, or a spring loader. A
  // pallet's packs stand as deep as they were planned, from its lane's back.
  const placement = getItemDepthPlacement(item);
  const { unitDepth, backZ } = placement;
  const unitsDeep = unitDepth > 0 ? placement.unitsDeep : 0;
  const zBack = item.shelf.getZBack();

  useEffect(() => {
    const marker = itemFrontOverhangMarker(item);
    if (!marker || unitsDeep === 0) return;
    const cleanUps = layout.columnXs.flatMap((x) => {
      const frontZ = zBack + backZ + unitDepth * unitsDeep;
      const shelfFrontZ = zBack + item.shelf.depth;
      const edge = createThreeMesh({
        scene: three.scene,
        width: item.getWidth(),
        height: 12,
        depth: 4,
        x: toSceneX(x),
        y: layout.yBottom + 6,
        z: frontZ + 1,
        color: marker.invalid ? OVERHANG_INVALID_COLOR : OVERHANG_COLOR,
        unlit: true,
        renderOrder: 4,
      });
      edge.mesh.userData = { type: "facing", item };
      const underside =
        frontZ > shelfFrontZ
          ? createThreeMesh({
              scene: three.scene,
              width: item.getWidth(),
              height: 4,
              depth: frontZ - shelfFrontZ,
              x: toSceneX(x),
              y: layout.yBottom - 4,
              z: shelfFrontZ,
              color: marker.invalid ? OVERHANG_INVALID_COLOR : OVERHANG_COLOR,
              unlit: true,
              renderOrder: 4,
            })
          : null;
      if (underside) underside.mesh.userData = { type: "facing", item };
      return underside ? [edge.cleanUp, underside.cleanUp] : [edge.cleanUp];
    });
    return () => cleanUps.forEach((cleanUp) => cleanUp());
  }, [item, three, toSceneX, layout, unitsDeep, zBack, backZ, unitDepth]);

  return (
    <>
      {Array.from({ length: totalFacings }).flatMap((_, index) => {
        const row = Math.floor(index / columns);
        const col = index % columns;

        return Array.from({ length: unitsDeep }).map((_, zIndex) => (
          <Facing
            key={`${index}-${zIndex}`}
            three={three}
            x={toSceneX(layout.columnXs[col] ?? layout.xLeft + item.getWidth() * col)}
            y={layout.yBottom + item.getHeight() * row}
            z={zBack + backZ + unitDepth * zIndex}
            item={item}
          />
        ));
      })}
    </>
  );
};
